/**
 * A company dossier.
 *
 * Contracts won and subsidies received are faceted lists scoped to this
 * company, so a supplier with hundreds of contracts is paged rather than
 * fetched whole. Ownership is split into current and ended holdings: listed
 * together, a company's former owners read as its current ones, which is
 * the most misleading thing this page could do.
 */

import { useQuery } from '@tanstack/react-query';
import { graphql } from '../gql';
import { request } from '../lib/graphql';
import { SubsidyList, TenderList } from '../components/lists';
import { DataTable } from '../components/DataTable';
import {
  Amount, FactPair, Badge, Breadcrumb, DetailHead, Disclosure, ErrorState, Fact, GraphButton, Link,
  Loading, SectionHead,
} from '../components/ui';
import { holding, num, shortHash, truncate, validity } from '../lib/format';
import { useHasScopedParams } from '../lib/router';

/* A second request, deliberately. It is the one query on this page that
   can put a named person beside an elected office, so the dossier renders
   whether or not it answers and nothing else waits on it. */
const COUNCILLOR_MATCHES = graphql(`
  query CompanyCouncillorMatches($uic: ID!) {
    companyCouncillorMatches(uic: $uic) {
      kind role personHash name roles councillorHash councillorName
      municipality municipalityKey electedYear sitting
      namesakes awards value
    }
    # The same question asked of families: a Bulgarian name states its own
    # genealogy, so a relative in office is findable where the person's
    # own name is not.
    companyFamilyTies(uic: $uic) {
      kind role personHash name roles councillorHash councillorName
      municipality municipalityKey electedYear sitting
      namesakes awards value
    }
  }
`);

const COMPANY = graphql(`
  query CompanyDossier($uic: ID!) {
    companies(where: { uic: $uic }) {
      uic
      name
      legal_form
      status
      seatedIn { key name province }
      administers { key name province }
      sig_computed_at
      sig_awards
      sig_single_bid sig_single_bid_value
      sig_direct_award sig_direct_award_value
      sig_subcontracted sig_shared_awards
      sig_buyers sig_top_buyer_key sig_top_buyer_name sig_top_buyer_share sig_top_buyer_total
      sig_mandate_awards sig_mandate_value sig_mandate_officials
      sig_cobidders sig_holdings_undated
      partOf { uic name }
      units { uic name }
      ownersConnection {
        edges {
          properties { share_percent share_amount share_currency valid_from valid_to }
          node { person_hash name }
        }
      }
      managersConnection {
        edges { properties { role valid_from valid_to } node { person_hash name } }
      }
      corporateOwnersConnection {
        edges {
          properties { share_percent share_amount share_currency valid_from valid_to }
          node { uic name }
        }
      }
      subsidiariesConnection {
        edges { properties { share_percent share_amount share_currency } node { uic name } }
      }
      membershipsConnection {
        edges { properties { share_percent is_lead } node { uic name } }
      }
      taxDebts { record_id amount debt_type date resolved }
      treasuryPayments {
        payment_id amount amount_bgn amount_eur payment_count
        first_date last_date match_type as_of breakdown_complete
        payer_names payer_amounts payer_counts
      }
    }
    tendersAggregate(where: { winners_SOME: { uic: $uic } }) { count value_bgn { sum } }
    procuredAggregate: tendersAggregate(where: { procuredBy_SOME: { uic: $uic } }) {
      count
      value_bgn { sum }
    }
    subsidiesAggregate(where: { beneficiaries_SOME: { uic: $uic } }) { count amount { sum } }
  }
`);

type Edge = { properties: { valid_to?: string | null } };

/* An edge with no valid_to is in force; the register's daily deeds close
   the others. */
function split<T extends Edge>(edges: readonly T[]) {
  const current: T[] = [];
  const former: T[] = [];
  for (const edge of edges) (edge.properties.valid_to ? former : current).push(edge);
  former.sort((a, b) => String(b.properties.valid_to ?? '')
    .localeCompare(String(a.properties.valid_to ?? '')));
  return { current, former };
}

/**
 * A councillor's name beside a board member's, where the board is read.
 *
 * Put only in the ownership tables and only for the municipality this
 * company is registered in. Matched against every council in the country
 * 1,505 companies collect a namesake somewhere, which is what a shared
 * name costs and not a finding; restricted to the company's own seat, 359
 * do, and a person is far likelier to run a business where they live and
 * sit on the council.
 *
 * The wording claims a coincidence and nothing more, because nothing more
 * can be claimed: ЦИК publishes no ЕГН and the register publishes it
 * hashed, so the two records cannot be joined into a person.
 */
/** What a tie is called, and how it is worded to the reader. */
export const TIE_LABELS: Record<string, string> = {
  'same-name': 'същото име',
  sibling: 'общ баща по име',
  'officer-is-parent': 'вероятно родител на',
  'official-is-parent': 'вероятно дете на',
};

function CouncillorMark({ match }: Readonly<{
  match: { kind: string; role: string; councillorName?: string | null;
    municipality?: string | null; electedYear: number;
    namesakes: number; value: number } | undefined;
}>) {
  if (!match) return null;
  const weak = match.namesakes > 1;
  const relation = TIE_LABELS[match.kind] ?? match.kind;
  return (
    <div className="uic">
      <Badge kind={weak ? 'record' : 'debt'}>
        {match.kind === 'same-name'
          ? `име на ${match.role}`
          : `роднинска връзка по име с ${match.role}`}
      </Badge>{' '}
      <span title={`${relation}: ${match.councillorName ?? ''} — ${match.role} в `
        + `${match.municipality ?? ''}, мандат ${match.electedYear}. `
        + (weak
          ? `${match.namesakes} лица с това бащино и фамилно име държат фирми `
            + 'в регистъра, така че връзката е слаба. '
          : 'Само едно лице с това бащино и фамилно име държи фирми в регистъра. ')
        + 'Роднинството не е установено — извежда се от формата на имената, а '
        + 'гражданският регистър не е публичен.'}
      >
        {`${relation} ${truncate(match.councillorName ?? '', 26)}`}
        {match.value > 0 ? ' · фирмата е взела поръчки от общината' : ''}
      </span>
    </div>
  );
}

export function CompanyPage({ uic }: { uic: string }) {
  const { data, isPending, isError, error } = useQuery({
    queryKey: ['company', uic],
    queryFn: () => request(COMPANY, { uic }),
  });
  /* A section decides for itself whether to open, unless the link the
     reader followed has already decided: a shared URL carrying a filter
     must arrive with the section that filter belongs to open, even where
     the section holds nothing at all. Read before the early returns,
     because hooks cannot be called conditionally. */
  const councillorMatches = useQuery({
    queryKey: ['company-councillors', uic],
    queryFn: () => request(COUNCILLOR_MATCHES, { uic }),
  });

  const deepLinked = {
    won: useHasScopedParams('t.'),
    awarded: useHasScopedParams('p.'),
    subsidies: useHasScopedParams('s.'),
  };

  if (isPending) return <Loading variant="cards" rows={6} />;
  if (isError) return <ErrorState message={(error).message} />;

  const company = data.companies[0];
  if (!company) return <ErrorState message="Фирмата не е намерена в графа." />;

  const owners = split(company.ownersConnection.edges);
  const managers = split(company.managersConnection.edges);
  const corporate = split(company.corporateOwnersConnection.edges);
  const seat = company.seatedIn[0];
  const debts = company.taxDebts;
  const treasury = company.treasuryPayments[0];
  /* The payer lists are optional in the schema: a payment node written
     before the breakdown existed carries none, and so does a company the
     mirror knows only a total for. */
  /* Which people on this board carry a councillor's name, so the badge can
     sit beside them where they are read rather than only in a section
     further down. */
  /* Same-name matches first, then the family ties — one list, because to
     a reader they are the same question asked with different strictness,
     and `kind` says which. Sorted so money and a rare name float up. */
  const matches = [
    ...(councillorMatches.data?.companyCouncillorMatches ?? []),
    ...(councillorMatches.data?.companyFamilyTies ?? []),
  ].sort((a, b) => (b.value - a.value) || (a.namesakes - b.namesakes));
  const flagged = new Map<string, (typeof matches)[number]>();
  for (const match of matches) {
    const held = flagged.get(match.personHash);
    // the strongest tie wins the badge: money first, then the rarer name
    if (!held || match.value > held.value
        || (match.value === held.value && match.namesakes < held.namesakes)) {
      flagged.set(match.personHash, match);
    }
  }

  const payers = (treasury?.payer_names ?? []).map((name, i) => ({
    rank: i,
    name: name ?? '—',
    amount: treasury?.payer_amounts?.[i] ?? 0,
    count: treasury?.payer_counts?.[i] ?? 0,
  }));

  return (
    <>
      <Breadcrumb>{[
        <Link key="up" href="#/companies">Фирми</Link>,
        company.name ?? uic,
      ]}</Breadcrumb>

      <DetailHead
        eyebrow={company.administers.length ? 'Общинска администрация' : 'Търговско дружество'}
        title={company.name ?? uic}
        meta={[
          ['ЕИК', <span className="mono">{uic}</span>] as const,
          company.legal_form ? ['Правна форма', company.legal_form] as const : null,
          company.status ? ['Състояние', company.status] as const : null,
          seat ? ['Седалище', (
            <Link href={`#/municipalities/${encodeURIComponent(seat.key)}`}>{seat.name}</Link>
          )] as const : null,
        ]}
        actions={(
          <>
            <GraphButton href={`#/graph?uic=${encodeURIComponent(uic)}`} />
            {' '}
            <GraphButton href={`#/activity?company=${encodeURIComponent(uic)}`}>
              Карта на дейността
            </GraphButton>
          </>
        )}
      />

      <div className="facts">
        {/* Each pair carries its own average, because the average is the
            reading: 600 contracts and 11.1bn are two register facts, and
            18.6m per contract is what says what kind of buyer this is.
            Getting it used to mean scrolling the list and dividing. */}
        <FactPair
          count={data.tendersAggregate.count}
          countLabel="Спечелени поръчки"
          total={data.tendersAggregate.value_bgn?.sum}
          totalLabel="Стойност на спечелените"
          averageLabel="Средно на спечелена"
          format={(average) => <Amount value={average} />}
        />
        {/* Only for bodies that award: an empty pair on every ordinary
            company would crowd out the figures that mean something. */}
        {data.procuredAggregate.count > 0 && (
          <FactPair
            count={data.procuredAggregate.count}
            countLabel="Възложени поръчки"
            total={data.procuredAggregate.value_bgn?.sum}
            totalLabel="Стойност на възложените"
            averageLabel="Средно на възложена"
            format={(average) => <Amount value={average} />}
          />
        )}
        <FactPair
          count={data.subsidiesAggregate.count}
          countLabel="Субсидии"
          total={data.subsidiesAggregate.amount?.sum}
          totalLabel="Стойност на субсидиите"
          averageLabel="Средно на субсидия"
          format={(average) => <Amount value={average} />}
        />
        {debts.length > 0 && <Fact value={num(debts.length)} label="Данъчни задължения" />}
        {treasury && <Fact value={<Amount value={treasury.amount ?? 0} />} label="Платено от бюджета" />}
      </div>

      {/* What the procurement record shows about this company. Components
          only — see api/schema.graphql and etl/ring_signals.py for why
          there is no score here. */}
      {company.sig_computed_at && (company.sig_single_bid || company.sig_direct_award
        || company.sig_mandate_awards || company.sig_subcontracted
        || (company.sig_top_buyer_share ?? 0) >= 0.5) && (
        <section className="block">
          <SectionHead title="Признаци в поръчките"
            aside="наблюдения, не заключения" />
          <dl className="detail-meta detail-meta-wide">
            {(company.sig_single_bid ?? 0) > 0 && (
              <div>
                <dt>Поръчки с единствена оферта</dt>
                <dd>
                  {num(company.sig_single_bid)} от {num(company.sig_awards)}
                  {' · '}<Amount value={company.sig_single_bid_value} />
                </dd>
              </div>
            )}
            {(company.sig_direct_award ?? 0) > 0 && (
              <div>
                <dt>Директно възлагане</dt>
                <dd>
                  {num(company.sig_direct_award)} от {num(company.sig_awards)}
                  {' · '}<Amount value={company.sig_direct_award_value} />
                </dd>
              </div>
            )}
            {(company.sig_top_buyer_share ?? 0) > 0 && (
              <div>
                <dt>Най-голям дял от възложител</dt>
                <dd>
                  {`${((company.sig_top_buyer_share ?? 0) * 100).toFixed(1)}% от `}
                  {company.sig_top_buyer_key
                    ? (
                      <Link href={`#/municipalities/${encodeURIComponent(company.sig_top_buyer_key)}`}>
                        {company.sig_top_buyer_name}
                      </Link>
                    )
                    : company.sig_top_buyer_name}
                  {' ('}<Amount value={company.sig_top_buyer_total} />{' общо)'}
                </dd>
              </div>
            )}
            {(company.sig_mandate_awards ?? 0) > 0 && (
              <div>
                <dt>Възложено от община със свой кмет</dt>
                <dd className="debt">
                  {num(company.sig_mandate_awards)} поръчки · <Amount value={company.sig_mandate_value} className="debt" />
                  {(company.sig_holdings_undated ?? 0) > 0
                    && ' · датата на дела не е публикувана, съвпадението е предполагаемо'}
                </dd>
              </div>
            )}
            {(company.sig_subcontracted ?? 0) > 0 && (
              <div>
                <dt>С подизпълнители</dt>
                <dd>{num(company.sig_subcontracted)} поръчки</dd>
              </div>
            )}
            {(company.sig_cobidders ?? 0) > 0 && (
              <div>
                <dt>Свързани фирми със същия възложител</dt>
                <dd>{num(company.sig_cobidders)}</dd>
              </div>
            )}
            {(company.sig_shared_awards ?? 0) > 0 && (
              <div>
                <dt>Споделени поръчки</dt>
                <dd>
                  {num(company.sig_shared_awards)} — стойността им се брои изцяло за всеки
                  изпълнител, защото източникът не публикува разпределение
                </dd>
              </div>
            )}
          </dl>
        </section>
      )}

      {(owners.current.length > 0 || managers.current.length > 0
        || corporate.current.length > 0 || company.membershipsConnection.edges.length > 0) && (
        <Disclosure title="Собственост и управление">
          <div className="grid grid-2">
            {owners.current.length > 0 && (
              <div>
                <h3 className="eyebrow">Собственици</h3>
                <DataTable
                  rows={owners.current}
                  rowKey={(edge) => edge.node.person_hash}
                  columns={[
                    {
                      key: 'who',
                      label: 'Лице',
                      render: (edge) => (
                        <div>
                          <Link href={`#/people/${edge.node.person_hash}`}>
                            {edge.node.name ?? shortHash(edge.node.person_hash)}
                          </Link>
                          <CouncillorMark match={flagged.get(edge.node.person_hash)} />
                        </div>
                      ),
                    },
                    { key: 'share', label: 'Дял', num: true, render: (edge) => holding(edge.properties) },
                    { key: 'since', label: 'От', render: (edge) => validity(edge.properties) },
                  ]}
                />
              </div>
            )}

            {managers.current.length > 0 && (
              <div>
                <h3 className="eyebrow">Управление</h3>
                <DataTable
                  rows={managers.current}
                  rowKey={(edge) => edge.node.person_hash}
                  columns={[
                    {
                      key: 'who',
                      label: 'Лице',
                      render: (edge) => (
                        <div>
                          <Link href={`#/people/${edge.node.person_hash}`}>
                            {edge.node.name ?? shortHash(edge.node.person_hash)}
                          </Link>
                          <CouncillorMark match={flagged.get(edge.node.person_hash)} />
                        </div>
                      ),
                    },
                    { key: 'role', label: 'Длъжност', render: (edge) => edge.properties.role ?? '—' },
                    { key: 'since', label: 'От', render: (edge) => validity(edge.properties) },
                  ]}
                />
              </div>
            )}

            {corporate.current.length > 0 && (
              <div>
                <h3 className="eyebrow">Дружества-съдружници</h3>
                <DataTable
                  rows={corporate.current}
                  rowKey={(edge) => edge.node.uic}
                  columns={[
                    {
                      key: 'who',
                      label: 'Дружество',
                      render: (edge) => (
                        <Link href={`#/companies/${edge.node.uic}`}>
                          {edge.node.name ?? edge.node.uic}
                        </Link>
                      ),
                    },
                    { key: 'share', label: 'Дял', num: true, render: (edge) => holding(edge.properties) },
                  ]}
                />
              </div>
            )}

            {company.membershipsConnection.edges.length > 0 && (
              <div>
                <h3 className="eyebrow">Участие в обединения</h3>
                <DataTable
                  rows={company.membershipsConnection.edges}
                  rowKey={(edge) => edge.node.uic}
                  columns={[
                    {
                      key: 'who',
                      label: 'ДЗЗД / обединение',
                      render: (edge) => (
                        <Link href={`#/companies/${edge.node.uic}`}>
                          {truncate(edge.node.name ?? edge.node.uic, 40)}
                        </Link>
                      ),
                    },
                    { key: 'share', label: 'Дял', num: true, render: (edge) => holding(edge.properties) },
                    {
                      key: 'lead',
                      label: '',
                      render: (edge) => (edge.properties.is_lead
                        ? <Badge kind="consortium">водещ</Badge> : null),
                    },
                  ]}
                />
              </div>
            )}
          </div>
        </Disclosure>
      )}

      {/* Who held this company before, and when. A contract awarded in 2021
          was awarded to whoever owned it in 2021, which is frequently not
          who owns it now. */}
      {(owners.former.length > 0 || managers.former.length > 0 || corporate.former.length > 0) && (
        <Disclosure title="Предишни собственици и управители"
          aside="по вписвания в Търговския регистър">
          <div className="grid grid-2">
            {owners.former.length > 0 && (
              <div>
                <h3 className="eyebrow">Собственици</h3>
                <DataTable
                  rows={owners.former}
                  rowKey={(edge) => `${edge.node.person_hash}:${edge.properties.valid_to}`}
                  columns={[
                    {
                      key: 'who',
                      label: 'Лице',
                      render: (edge) => (
                        <Link href={`#/people/${edge.node.person_hash}`}>
                          {edge.node.name ?? shortHash(edge.node.person_hash)}
                        </Link>
                      ),
                    },
                    { key: 'period', label: 'Период', render: (edge) => validity(edge.properties) },
                    { key: 'share', label: 'Дял', num: true, render: (edge) => holding(edge.properties) },
                  ]}
                />
              </div>
            )}

            {managers.former.length > 0 && (
              <div>
                <h3 className="eyebrow">Управители</h3>
                <DataTable
                  rows={managers.former}
                  rowKey={(edge) => `${edge.node.person_hash}:${edge.properties.valid_to}`}
                  columns={[
                    {
                      key: 'who',
                      label: 'Лице',
                      render: (edge) => (
                        <Link href={`#/people/${edge.node.person_hash}`}>
                          {edge.node.name ?? shortHash(edge.node.person_hash)}
                        </Link>
                      ),
                    },
                    { key: 'role', label: 'Длъжност', render: (edge) => edge.properties.role ?? '—' },
                    { key: 'period', label: 'Период', render: (edge) => validity(edge.properties) },
                  ]}
                />
              </div>
            )}

            {corporate.former.length > 0 && (
              <div>
                <h3 className="eyebrow">Дружества-съдружници</h3>
                <DataTable
                  rows={corporate.former}
                  rowKey={(edge) => `${edge.node.uic}:${edge.properties.valid_to}`}
                  columns={[
                    {
                      key: 'who',
                      label: 'Дружество',
                      render: (edge) => (
                        <Link href={`#/companies/${edge.node.uic}`}>
                          {edge.node.name ?? edge.node.uic}
                        </Link>
                      ),
                    },
                    { key: 'period', label: 'Период', render: (edge) => validity(edge.properties) },
                    { key: 'share', label: 'Дял', num: true, render: (edge) => holding(edge.properties) },
                  ]}
                />
              </div>
            )}
          </div>
        </Disclosure>
      )}

      {treasury && (
        <Disclosure
          title="Плащания от държавния бюджет"
          aside={`СЕБРА, до ${treasury.as_of ?? '—'}`}
        >
          <dl className="detail-meta detail-meta-wide">
            <div>
              <dt>Общо получено</dt>
              <dd><Amount value={treasury.amount ?? 0} /></dd>
            </div>
            <div>
              <dt>Плащания</dt>
              <dd>
                {num(treasury.payment_count ?? 0)}
                {treasury.first_date && treasury.last_date
                  ? ` · ${treasury.first_date} – ${treasury.last_date}` : ''}
              </dd>
            </div>
            {(treasury.amount_eur ?? 0) > 0 && (
              <div>
                {/* The feed totals each currency separately, so the sum
                    above converts the euro half at the fixed rate. */}
                <dt>По валута</dt>
                <dd>
                  <Amount value={treasury.amount_bgn ?? 0} /> + <Amount value={treasury.amount_eur ?? 0} currency="EUR" />
                </dd>
              </div>
            )}
            {treasury.match_type && treasury.match_type !== 'exact' && (
              <div>
                {/* A name match is a weaker claim than one on the identifier
                    and the reader is entitled to know which this is. */}
                <dt>Свързване</dt>
                <dd>по име, не по ЕИК</dd>
              </div>
            )}
          </dl>

          {payers.length > 0 && (
            <>
              <SectionHead
                title="Кой плаща"
                aside={treasury.breakdown_complete
                  ? 'всички разпоредители'
                  : `най-големите ${payers.length}; сумата им е по-малка от общата`}
              />
              <DataTable
                rows={payers}
                rowKey={(row) => String(row.rank)}
                columns={[
                  { key: 'payer', label: 'Разпоредител', render: (row) => row.name },
                  {
                    key: 'count',
                    label: 'Плащания',
                    num: true,
                    render: (row) => num(row.count),
                  },
                  {
                    key: 'amount',
                    label: 'Сума',
                    num: true,
                    render: (row) => <Amount value={row.amount} />,
                  },
                ]}
              />
            </>
          )}
        </Disclosure>
      )}

      {debts.length > 0 && (
        <Disclosure title="Данъчни задължения" aside="по данни на НАП">
          <DataTable
            rows={debts}
            rowKey={(row) => row.record_id}
            columns={[
              { key: 'type', label: 'Вид', render: (row) => row.debt_type ?? '—' },
              { key: 'date', label: 'Дата', render: (row) => row.date ?? '—' },
              {
                key: 'amount',
                label: 'Сума',
                num: true,
                render: (row) => <Amount value={row.amount} />,
              },
              {
                key: 'state',
                label: '',
                render: (row) => (row.resolved
                  ? 'погасено'
                  : <Badge kind="debt">непогасено</Badge>),
              },
            ]}
          />
        </Disclosure>
      )}

      {matches.length > 0 && (
        <Disclosure
          title="Съвпадения с изборни лица"
          aside="за проверка"
          defaultOpen={matches.some((match) => match.value > 0)}
        >
          <p className="dossier-prose">
            Лице от ръководството на тази фирма носи име, което съвпада с това на
            изборно лице в общината, в която фирмата е регистрирана — същото име,
            или име, чиято форма подсказва роднинство. Българското име носи
            бащиното си: общо бащино и фамилно име значи баща с едно и също име,
            а собствено име, което образува чуждото бащино, значи баща и дете.
            Изборно лице може да има бизнес, както и семейството му — законът го
            позволява.{' '}
            <strong>Това не е установена самоличност.</strong> Съветниците идват
            от протоколите на ЦИК, които не публикуват ЕГН, а собствениците и
            управителите — от Търговския регистър, който го публикува вече
            хеширан; двата записа за едно и също лице не могат да бъдат свързани.
            Съвпадат име и населено място. Проверката се прави само за общината
            по седалище: срещу всички съвети в страната 1 505 фирми намират
            съименник някъде, което е цената на често срещано име, а не находка.
            Роднинството също не е установено — то се извежда от формата на
            имената, а гражданският регистър не е публичен. Колоната „лица с
            това име“ показва колко души с това бащино и фамилно име държат фирми:
            1 прави съвпадението голямо, 101 го прави безполезно.
            Дали има конфликт на интереси зависи от декларациите по ЗПК и от
            протоколите на съвета, които не се публикуват машинночетимо.
          </p>
          <DataTable
            rows={matches}
            rowKey={(row) => `${row.kind}:${row.personHash}:${row.councillorHash}`
              + `:${row.municipalityKey}:${row.electedYear}`}
            columns={[
              {
                key: 'person',
                label: 'Лице от ръководството',
                render: (row) => (
                  <div>
                    <Link href={`#/people/${row.personHash}`}>
                      {row.name ?? shortHash(row.personHash)}
                    </Link>
                    <div className="uic">
                      {row.roles.map((role) => (role === 'OWNER' ? 'собственик' : 'управител')).join(', ')}
                    </div>
                  </div>
                ),
              },
              {
                key: 'relation',
                label: 'Връзка',
                render: (row) => (
                  <div>
                    {TIE_LABELS[row.kind] ?? row.kind}
                    {row.kind !== 'same-name' && row.councillorName && (
                      <div className="uic">{truncate(row.councillorName, 30)}</div>
                    )}
                  </div>
                ),
              },
              {
                key: 'council',
                label: 'Изборна длъжност в',
                render: (row) => (
                  <div>
                    <Link href={`#/municipalities/${encodeURIComponent(row.municipalityKey)}`}>
                      {row.municipality}
                    </Link>
                    <div className="uic">
                      {`${row.role} · мандат ${row.electedYear}`}
                    </div>
                  </div>
                ),
              },
              {
                key: 'namesakes',
                label: 'Лица с това име',
                num: true,
                render: (row) => (row.namesakes === 1
                  ? (
                    <span className="amount"
                      title="Само едно лице с това име държи фирми в регистъра">
                      1
                    </span>
                  )
                  : (
                    <span className="muted"
                      title="Няколко лица с това име държат фирми — съвпадението е слабо">
                      {num(row.namesakes)}
                    </span>
                  )),
              },
              {
                /* Zero is the case where the rules worked, and saying so is
                   the point: a councillor's business that has never been
                   paid by their own municipality belongs in this table as
                   much as one that has. */
                key: 'value',
                label: 'Поръчки от тази община',
                num: true,
                render: (row) => (row.awards > 0
                  ? (
                    <div>
                      <Amount value={row.value} />
                      <div className="uic mono">{`${num(row.awards)} поръчки`}</div>
                    </div>
                  )
                  : <span className="muted">няма</span>),
              },
            ]}
          />
        </Disclosure>
      )}

      {/* Rendered whether or not anything was won: a company that has
          never won a public contract is a fact worth stating on a site
          about public contracts, and an absent section reads as "we do
          not know" rather than "none". Folded shut when empty, so the
          statement costs a line instead of a screen. */}
      <Disclosure
        title="Спечелени поръчки"
        aside={`${num(data.tendersAggregate.count)} общо`}
        defaultOpen={data.tendersAggregate.count > 0 || deepLinked.won}
      >
        <TenderList prefix="t." scope={{ winnerUics: [uic] }} />
      </Disclosure>

      {/* What this body BOUGHT. For an institution this is the whole
          role: Агенция „Пътна инфраструктура" wins nothing and awards
          11.1bn, and without this section its page showed only the
          subsidies it received. Its own prefix ("p.") so filtering the
          procurements it awarded does not disturb the ones it won.

          Unlike the other two this one is omitted entirely when empty,
          for the same reason its figures are: awarding is a role almost
          no company has, and "Възложени поръчки — 0" on seventy thousand
          ordinary companies is not a finding, it is furniture. */}
      {(data.procuredAggregate.count > 0 || deepLinked.awarded) && (
        <Disclosure
          title="Възложени поръчки"
          aside={`${num(data.procuredAggregate.count)} общо`}
        >
          <TenderList prefix="p." scope={{ buyerUics: [uic] }} />
        </Disclosure>
      )}

      <Disclosure
        title="Получени субсидии"
        aside={`${num(data.subsidiesAggregate.count)} общо`}
        defaultOpen={data.subsidiesAggregate.count > 0 || deepLinked.subsidies}
      >
        <SubsidyList prefix="s." scope={{ beneficiaryUics: [uic] }} />
      </Disclosure>
    </>
  );
}
